import apiClient from '@/api/apiClient';
import type {
  DecisionOutcome,
  AnalystRiskLevel,
} from '@/composables/useKycDecisionDraft';
import type { ParecerDashboardSummary } from '@/types/parecerDashboard';

// --- Type Definitions ---

export type ParecerStage =
  | 'pending'
  | 'under_analysis'
  | 'manager_review'
  | 'request_for_information'
  | 'finalized';

/** The workflow's unassigned queue: the parecer exists but nobody owns it yet. */
export const PARECER_QUEUE_STAGE_SLUG: ParecerStage = 'pending';

/** Where a parecer is actually worked. Taking one out of the queue lands it here. */
export const PARECER_START_STAGE_SLUG: ParecerStage = 'under_analysis';

export type ParecerDecisionRole = 'proposal' | 'final';

export type ParecerPdfStatus = 'processing' | 'completed' | 'failed';

export interface ParecerPdfStatusResponse {
  status: ParecerPdfStatus;
  signedUrl: string | null;
  completedAt: string | null;
  errorMessage: string | null;
}

export interface ParecerDecision {
  id: string;
  parecer_id: string;
  decision_role: ParecerDecisionRole;
  decision: DecisionOutcome | null;
  reason_code: string | null;
  secondary_reason_code: string | null;
  justification: string | null;
  risk_level: AnalystRiskLevel | null;
  risk_justification: string | null;
  expiration_date: string | null;
  decided_at: string | null;
  decided_by_user_id: string | null;
  decided_by_user_name?: string | null;
}

export interface ParecerFlagComment {
  id: string;
  parecer_id: string;
  category_slug: string;
  category_instance_id: string | null;
  entity_tax_id: string;
  entity_type: string;
  entity_name: string;
  comment: string;
}

export interface ParecerFile {
  id: string;
  parecer_id: string;
  file_id: string;
  display_name: string;
  file_size_bytes: number | null;
  description: string | null;
  category_slug: string | null;
  inserted_at: string;
  inserted_by_user_name?: string | null;
  signed_url?: string;
}

export interface ParecerRelationshipType {
  relationship_type_id: string;
  name: string | null;
  slug: string | null;
}

export interface ParecerStageInfo {
  id: string;
  slug: ParecerStage;
  name: string;
  description?: string;
  is_editable: boolean;
}

export interface ParecerStageHistoryItem {
  id: string;
  from_stage_slug: string | null;
  to_stage_slug: string;
  transitioned_at: string;
  transitioned_by_user_id: string | null;
  transitioned_by_user_name?: string | null;
  comment: string | null;
}

export interface ManifestFieldConfig {
  visible: boolean;
  editable: boolean;
  required: boolean;
  visible_for_roles?: string[];
  editable_for_roles?: string[];
  editable_requires_assignee_for_roles?: string[];
}

export interface ParecerWorkflowStageManifest {
  id: string;
  slug: string;
  name: string;
  sequence: number;
  is_initial: boolean;
  is_final: boolean;
}

export interface ParecerStageRolePermission {
  id: string;
  slug: string;
  name: string;
  can_view: boolean;
  can_edit: boolean;
  must_be_assignee: boolean;
}

export interface ParecerStageUiManifest {
  global: {
    is_editable: boolean;
    lock_reason: string | null;
    stage_slug: string;
    is_final: boolean;
  };
  fields: Record<string, ManifestFieldConfig>;
  roles?: ParecerStageRolePermission[];
}

export interface ParecerUiManifest {
  global: {
    is_editable: boolean;
    lock_reason: string | null;
    stage_slug: string;
    is_final: boolean;
  };
  fields: Record<string, ManifestFieldConfig>;
  initial_stage_slug?: string;
  /** The stage a new parecer opens in. NOT initial_stage_slug — that one is the 'pending' queue. */
  start_stage_slug?: string;
  selected_stage_slug?: string;
  current_user_role?: {
    id: string;
    slug: string;
    name: string;
  } | null;
  stage_manifests?: Record<string, ParecerStageUiManifest>;
  stages?: ParecerWorkflowStageManifest[];
}

export interface Parecer {
  id: string;
  report_request_id: string;
  entity_type: 'person' | 'company';
  entity_tax_id: string;
  entity_name: string;
  report_risk_level: string | null;
  report_generated_at: string | null;
  final_decision?: ParecerDecision | null;
  current_stage: ParecerStageInfo;
  workflow_id: string;
  account_id: string;
  responsible_user_id: string | null;
  assigned_user_id: string | null;
  assigned_user_name?: string | null;
  created_by?: 'user' | 'automation' | null;
  policy_decision?: 'approve' | 'decline' | 'manual' | null;
  priority: string | null;
  inserted_at: string;
  updated_at: string | null;
  decisions: Partial<Record<ParecerDecisionRole, ParecerDecision | null>>;
  flag_comments: ParecerFlagComment[];
  files: ParecerFile[];
  relationship_types: ParecerRelationshipType[];
  stage_history: ParecerStageHistoryItem[];
}

/**
 * A risk level recorded on a parecer, attributed to the stage that recorded it.
 *
 * Authority does NOT come from `decision_role` — which role may edit a stage is configurable
 * per account, so role names carry no fixed seniority. The workflow stage `sequence` is the
 * ordering that holds: a later stage is a later word on the matter.
 */
export interface ParecerRiskReading {
  stage_slug: string | null;
  stage_name: string | null;
  stage_sequence: number | null;
  decision_role: string;
  risk_level: string;
}

export interface ParecerListItem {
  id: string;
  report_request_id: string;
  entity_type: 'person' | 'company';
  entity_tax_id: string;
  entity_name: string;
  current_stage: ParecerStageInfo;
  priority: string | null;
  inserted_at: string;
  updated_at: string | null;
  // Fields added for the global parecer section. Optional so existing consumers
  // (useParecerQueries and its fixtures) that build a minimal item still type-check;
  // the backend list projection always populates them.
  workflow_id?: string;
  report_risk_level?: string | null;
  report_generated_at?: string | null;
  created_by?: string | null;
  /** What the decision policy decided at open time; with created_by, flags automation rows. */
  policy_decision?: 'approve' | 'decline' | 'manual' | null;
  responsible_user_id?: string | null;
  responsible_user_name?: string | null;
  assigned_user_name?: string | null;
  /** Final decision fields, only set once the parecer is finalized. */
  final_decision_outcome?: DecisionOutcome | null;
  final_decision_at?: string | null;
  final_decision_expiration_date?: string | null;
  /**
   * Every human risk reading, ordered by workflow stage sequence (weakest authority first),
   * to contrast against the policy's `report_risk_level`. The LAST entry governs.
   */
  risk_trail?: ParecerRiskReading[];
  /** Risk policy the underlying report was generated against. */
  risk_profile_name?: string | null;
  risk_profile_version?: number | null;
  /** Relationship types linked to the parecer, as display names. */
  relationship_type_names?: string[];
  /** When the parecer entered its current stage (basis for time-in-stage). */
  current_stage_since?: string | null;
}

export interface ParecerListResponse {
  items: ParecerListItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface ParecerCreatePayload {
  report_request_id: string;
  entity_type: 'person' | 'company';
  entity_tax_id: string;
  entity_name: string;
  report_risk_level?: string | null;
  report_generated_at?: string | null;
}

export interface ParecerDecisionPatch {
  decision?: DecisionOutcome | null;
  reason_code?: string | null;
  secondary_reason_code?: string | null;
  justification?: string | null;
  risk_level?: AnalystRiskLevel | null;
  risk_justification?: string | null;
  expiration_date?: string | null;
}

export interface ParecerFlagCommentDelta {
  action_type: 'add' | 'update' | 'delete';
  ids?: string[];
  items?: Array<{
    id?: string;
    category_slug: string;
    entity_tax_id: string;
    entity_name: string;
    entity_type: string;
    comment: string;
  }>;
}

export interface ParecerRelationshipTypeDelta {
  relationship_type_slugs: string[];
}

export interface ParecerDraftPatch {
  decision?: ParecerDecisionPatch;
  relationship_types?: ParecerRelationshipTypeDelta;
  flag_comments?: ParecerFlagCommentDelta;
}

export interface ManagerDecisionPayload {
  // Stage the user is acting on; backend rejects if it no longer matches the parecer's
  // current stage (stale page) and checks edit permission on it.
  workflow_stage_id: string;
  decision: DecisionOutcome;
  comment: string;
  risk_level: AnalystRiskLevel;
  expiration_date?: string | null;
  draft_patch?: ParecerDraftPatch;
  deleted_file_ids?: string[];
}

export interface ManagerDecisionDraftPayload {
  decision: DecisionOutcome | null;
  comment: string;
  risk_level: AnalystRiskLevel | null;
  expiration_date: string | null;
}

export interface AnalystDecisionSubmitPayload {
  // Stage the user is acting on; backend rejects if it no longer matches the parecer's
  // current stage (stale page) and checks edit permission on it.
  workflow_stage_id: string;
  target_stage_slug?: string;
  draft_patch?: ParecerDraftPatch;
  deleted_file_ids?: string[];
}

export interface AvailableAction {
  to_stage_slug: string;
  label: string;
  is_disabled: boolean;
  reason: string | null;
  config: Record<string, unknown>;
  transition_condition: Record<string, unknown>;
}

export interface TransitionPayload {
  to_stage_slug: string;
  comment?: string;
}

export interface ParecerFilterParams {
  report_request_id?: string;
  entity_tax_id?: string;
  entity_type?: 'person' | 'company';
  /** @deprecated single-stage filter kept for useParecerQueries back-compat; prefer `status`. */
  stage?: ParecerStage;
  /** Multi-stage filter — maps to the backend `status` query param (one per kanban column). */
  status?: ParecerStage[];
  assigned_user_id?: string;
  responsible_user_id?: string;
  decision?: DecisionOutcome;
  risk_level?: string;
  created_by?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  sort_field?: string;
  sort_order?: number;
  page?: number;
  page_size?: number;
}

export interface ParecerFilterOption {
  value: string;
  label: string;
}

export interface ParecerFilterOptions {
  analysts: ParecerFilterOption[];
  created_by: string[];
  risk_levels: string[];
  decisions: string[];
}

// --- API Functions ---

const parecerApi = {
  /**
   * Create a new parecer and initialize the PROPOSAL decision row
   */
  async createParecer(payload: ParecerCreatePayload): Promise<Parecer> {
    return apiClient.post<Parecer>('/kyc-parecers/start', payload);
  },

  /**
   * Get a single parecer by ID with full details
   */
  async getParecer(parecerId: string): Promise<Parecer> {
    return apiClient.get<Parecer>(`/kyc-parecers/${parecerId}`);
  },

  /**
   * Get the latest generated PDF status for a finalized parecer.
   */
  async getPdfReportStatus(parecerId: string): Promise<ParecerPdfStatusResponse> {
    return apiClient.get<ParecerPdfStatusResponse>(
      `/kyc-parecers/${parecerId}/pdf-report/latest`,
      { timeout: 15_000 },
    );
  },

  /**
   * Get KYC parecer UI manifest for the current user.
   * When stageSlug is omitted, backend uses the initial workflow stage.
   */
  async getUiManifest(stageSlug?: string | null): Promise<ParecerUiManifest> {
    const params: Record<string, string> = {};
    if (stageSlug) {
      params.stage_slug = stageSlug;
    }
    return apiClient.get<ParecerUiManifest>('/kyc-parecers/ui-manifest', { params });
  },

  /**
   * List parecers with optional filters
   */
  async listParecers(params?: ParecerFilterParams): Promise<ParecerListResponse> {
    return apiClient.get<ParecerListResponse>('/kyc-parecers/', { params: params as Record<string, string | number | boolean | (string | number)[] | undefined> });
  },

  /**
   * Pipeline-overview metrics for the parecer dashboard.
   */
  async getDashboardSummary(params: { date_from?: string; date_to?: string } = {}): Promise<ParecerDashboardSummary> {
    return apiClient.get<ParecerDashboardSummary>('/kyc-parecers/dashboard-summary', { params });
  },

  /**
   * Distinct filter values (analysts / created_by / risk levels / decisions).
   */
  async getFilterOptions(): Promise<ParecerFilterOptions> {
    return apiClient.get<ParecerFilterOptions>('/kyc-parecers/filter-options');
  },

  /**
   * Get all parecers for a specific entity
   */
  async getParecersForEntity(
    entityTaxId: string,
    entityType?: 'person' | 'company'
  ): Promise<ParecerListResponse> {
    const params: Record<string, string> = { entity_tax_id: entityTaxId };
    if (entityType) {
      params.entity_type = entityType;
    }
    return apiClient.get<ParecerListResponse>('/kyc-parecers/', { params });
  },

  /**
   * Get parecer by report request ID
   */
  async getParecerByRequestId(requestId: string): Promise<Parecer | null> {
    try {
      const response = await apiClient.get<ParecerListResponse>('/kyc-parecers/', {
        params: { report_request_id: requestId, page_size: 1 },
      });
      return response.items[0] ? await this.getParecer(response.items[0].id) : null;
    } catch {
      return null;
    }
  },

  /**
   * Get available workflow stages for PARECER workflow type
   */
  async getParecerStages(): Promise<ParecerStageInfo[]> {
    return apiClient.get<ParecerStageInfo[]>('/kyc-parecers/stages');
  },

  /**
   * Get available workflow transitions for a parecer
   */
  async getAvailableActions(parecerId: string): Promise<AvailableAction[]> {
    return apiClient.get<AvailableAction[]>(`/kyc-parecers/${parecerId}/actions`);
  },

  /**
   * Save draft changes (decision fields + child collections)
   */
  async saveDraft(
    parecerId: string,
    patch: ParecerDraftPatch,
    files: File[] = [],
    deletedFileIds: string[] = [],
  ): Promise<Parecer> {
    const formData = new FormData();
    formData.append('payload_json', JSON.stringify(patch));
    formData.append('deleted_file_ids_json', JSON.stringify(deletedFileIds));
    files.forEach((file) => formData.append('files', file));
    return apiClient.patch<Parecer>(`/kyc-parecers/${parecerId}/draft`, formData);
  },

  /**
   * Submit analyst action; backend resolves workflow transition.
   */
  async submitAnalystDecision(
    parecerId: string,
    payload: AnalystDecisionSubmitPayload,
    files: File[] = [],
  ): Promise<Parecer> {
    const formData = new FormData();
    formData.append('payload_json', JSON.stringify(payload));
    files.forEach((file) => formData.append('files', file));
    return apiClient.post<Parecer>(`/kyc-parecers/${parecerId}/submit`, formData);
  },

  /**
   * Submit manager decision (transitions manager_review -> finalized)
   */
  async submitManagerDecision(
    parecerId: string,
    payload: ManagerDecisionPayload,
    files: File[] = [],
  ): Promise<Parecer> {
    const formData = new FormData();
    formData.append('payload_json', JSON.stringify(payload));
    files.forEach((file) => formData.append('files', file));
    return apiClient.post<Parecer>(`/kyc-parecers/${parecerId}/manager-decision`, formData);
  },

  /**
   * Manager returns to analyst (transitions manager_review -> under_analysis)
   */
  async returnToAnalyst(parecerId: string, comment: string): Promise<Parecer> {
    return apiClient.post<Parecer>(`/kyc-parecers/${parecerId}/return`, { comment });
  },

  /**
   * Initiate RFI (transitions under_analysis -> request_for_information)
   */
  async initiateRfi(parecerId: string, comment?: string): Promise<Parecer> {
    return apiClient.post<Parecer>(`/kyc-parecers/${parecerId}/rfi`, { comment });
  },

  /**
   * Resolve RFI (transitions request_for_information -> under_analysis)
   */
  async resolveRfi(parecerId: string): Promise<Parecer> {
    return apiClient.post<Parecer>(`/kyc-parecers/${parecerId}/rfi-received`, {});
  },

  /**
   * Generic workflow transition (delegates to workflow service)
   */
  async transitionParecer(parecerId: string, payload: TransitionPayload): Promise<Parecer> {
    return apiClient.post<Parecer>(`/kyc-parecers/${parecerId}/transition`, payload);
  },
};

export default parecerApi;
